<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <!-- Breadcrumb -->
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />

        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap">
          <div class="mb-2">
            <div class="input-icon-end position-relative" style="min-width: 260px">
              <input
                type="text"
                class="form-control date-range bookingrange"
                ref="dateRangeInput"
                placeholder="dd/mm/yyyy - dd/mm/yyyy"
              />
              <span class="input-icon-addon">
                <i class="ti ti-chevron-down"></i>
              </span>
            </div>
          </div>

          <div class="head-icons ms-2">
            <a id="collapse-header" @click="toggleHeader">
              <i class="ti ti-chevrons-up"></i>
            </a>
          </div>
        </div>
      </div>

      <!-- Summary -->
      <div class="row">
        <div class="col-xl-12">
          <div class="row">
            <div v-for="c in kpiCards" :key="c.title" class="col-md-3 d-flex">
              <div class="card flex-fill">
                <div class="card-body">
                  <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                    <div>
                      <span class="fs-14 fw-normal text-truncate mb-1">{{ c.title }}</span>
                      <h5 class="mb-0">
                        {{ currency }}
                        {{ Number(c.value || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
                      </h5>
                    </div>
                    <span class="avatar avatar-md avatar-rounded" :class="`bg-transparent-${c.cls} border border-${c.cls}`">
                      <span :class="`text-${c.cls}`"><i class="ti ti-receipt"></i></span>
                    </span>
                  </div>
                  <p class="fs-12 fw-normal d-flex align-items-center text-truncate mb-0">
                    <span class="text-muted">Based on selected period</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table -->
      <div class="card">
        <div class="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
          <div>
            <h5 class="mb-1">My Payslips</h5>
            <div class="text-muted fs-12">
              Download your payslips only. If a period is missing, payroll may not be processed yet.
            </div>
          </div>

          <button class="btn btn-outline-secondary btn-sm" :disabled="store.myPayslipLoading" @click="fetchList">
            <i class="ti ti-refresh me-1"></i>
            Refresh
          </button>
        </div>

        <div class="card-body p-0">
          <div class="custom-datatable-filter table-responsive">
            <a-table
              class="table datatable thead-light"
              :columns="columns"
              :data-source="dataSource"
              :loading="store.myPayslipLoading"
              :pagination="pagination"
              @change="onTableChange"
              row-key="key"
            >
              <template #bodyCell="{ column, record }">
                <!-- Period -->
                <template v-if="column.key === 'Period'">
                  <div class="d-flex flex-column">
                    <span class="fw-semibold">{{ record.period_label }}</span>
                    <span class="text-muted fs-12">{{ record.period }}</span>
                  </div>
                </template>

                <!-- Net Pay -->
                <template v-else-if="column.key === 'Net'">
                  <span class="fw-semibold">
                    {{ currency }}
                    {{ Number(record.net_pay || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) }}
                  </span>
                </template>

                <!-- Status -->
                <template v-else-if="column.key === 'Status'">
                  <span
                    class="badge"
                    :class="
                      record.payslip_status === 'emailed'
                        ? 'badge-soft-success'
                        : record.historical
                        ? 'badge-soft-info'
                        : 'badge-soft-warning'
                    "
                  >
                    {{ record.payslip_status || (record.historical ? "archive" : "available") }}
                  </span>
                </template>

                <!-- Download -->
                <template v-else-if="column.key === 'Action'">
                  <button
                    class="btn btn-primary btn-sm"
                    :disabled="!record.payslip_id && !record.historical"
                    @click="download(record)"
                  >
                    <i class="ti ti-download me-1"></i>
                    Download
                  </button>
                </template>
              </template>
            </a-table>

            <div v-if="!store.myPayslipLoading && dataSource.length === 0" class="p-4 text-center text-muted">
              No payslips found for the selected period.
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>
        Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a>
      </p>
    </div>
  </div>
</template>

<script setup>
import "daterangepicker/daterangepicker.css";
import "daterangepicker/daterangepicker.js";
import DateRangePicker from "daterangepicker";
import moment from "moment";
import { ref, reactive, computed, onMounted } from "vue";
import { useEmployeeSalaryStore } from "@/stores/employeeSalaryStore";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";

defineOptions({ name: "MyEmployeePayslips" });

const title = "My Payslips";
const text = "Employee";
const text1 = "My Payslips";

const store = useEmployeeSalaryStore();
const dateRangeInput = ref(null);

const filters = reactive({
  from: moment().startOf("year").format("YYYY-MM"),
  to: moment().format("YYYY-MM"),
  page: 1,
  per_page: 25,
});

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function initRangePicker() {
  if (!dateRangeInput.value) return;

  const start = moment(filters.from, "YYYY-MM").startOf("month");
  const end = moment(filters.to, "YYYY-MM").endOf("month");

  const onPick = (s, e) => {
    filters.from = moment(s).format("YYYY-MM");
    filters.to = moment(e).format("YYYY-MM");
    filters.page = 1;
    fetchList();
  };

  new DateRangePicker(
    dateRangeInput.value,
    {
      startDate: start,
      endDate: end,
      showDropdowns: true,
      autoApply: true,
      ranges: {
        Today: [moment(), moment()],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Year to Date": [moment().startOf("year"), moment().endOf("month")],
        "Last Year": [
          moment().subtract(1, "year").startOf("year"),
          moment().subtract(1, "year").endOf("year"),
        ],
      },
    },
    onPick
  );
}

async function fetchList() {
  await store.fetchMyPayslips({
    from: filters.from,
    to: filters.to,
    page: filters.page,
    per_page: filters.per_page,
  });
}

function onTableChange(pagination) {
  filters.page = pagination.current;
  filters.per_page = pagination.pageSize;
  fetchList();
}

const summary = computed(() => store.myPayslipSummary || {});
const currency = computed(() => summary.value?.currency || "KES");

const kpiCards = computed(() => [
  { title: "Total Gross", value: summary.value?.total_gross || 0, cls: "primary" },
  { title: "Deductions", value: summary.value?.total_deductions || 0, cls: "danger" },
  { title: "Net Pay", value: summary.value?.total_net || 0, cls: "success" },
  { title: "Allowances", value: summary.value?.total_allowances || 0, cls: "skyblue" },
]);

const columns = [
  { title: "Period", dataIndex: "period_label", key: "Period", sorter: (a, b) => (a.period || "").localeCompare(b.period || "") },
  { title: "Net Pay", dataIndex: "net_pay", key: "Net", sorter: (a, b) => (a.net_pay || 0) - (b.net_pay || 0) },
  { title: "Status", dataIndex: "payslip_status", key: "Status" },
  { title: "Action", key: "Action" },
];

const dataSource = computed(() =>
  (store.myPayslipRows || []).map((r, idx) => ({
    key: r.id || idx,
    id: r.id,
    period: r.period || r.payroll_run?.period || "",
    period_label: r.period_label || r.month_label ? `${r.month_label} ${r.year}` : r.period_label || r.period || "-",
    net_pay: r.net_pay ?? 0,
    payslip_id: r?.payslip?.id || null,
    payslip_status: r?.payslip?.status || (r.historical ? "archive" : "available"),
    historical: !!(r.historical || r.source === "imported_history"),
  }))
);

const pagination = computed(() => ({
  total: store.myPayslipTotal,
  pageSize: store.myPayslipPageSize,
  current: store.myPayslipCurrent,
  showTotal: (t) => `Total ${t} items`,
}));

async function download(row) {
  if (!row?.payslip_id && !row?.historical) return;
  if (row.historical) {
    try {
      const res = await reportsService.downloadMyHistoricalPayslip({ period: row.period });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `payslip-archive-${row.period}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast(err?.response?.data?.message || "Failed to download payslip.", "error");
    }
    return;
  }
  await store.downloadMyPayslip(row.payslip_id, { period: row.period_label || row.period });
}

onMounted(() => {
  initRangePicker();
  fetchList();
});
</script>


